import { useMemo } from 'react';
import { useSession } from '@/features/auth/store';
import { useLocale } from './i18n';
import { transliterateCyrillic } from './transliterate';

export type NamePart =
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'maidenName'
  | 'relationship';

export const NAME_PART_LABELS: Record<NamePart, string> = {
  lastName: 'Фамилия',
  firstName: 'Имя',
  middleName: 'Отчество',
  maidenName: 'Девичья фамилия',
  relationship: 'Родственная связь',
};

export const DEFAULT_NAME_FORMAT: NamePart[] = [
  'lastName',
  'maidenName',
  'firstName',
  'middleName',
  'relationship',
];

const VALID_PARTS = new Set<NamePart>([
  'firstName',
  'middleName',
  'lastName',
  'maidenName',
  'relationship',
]);

export function normalizeNameFormat(format?: string[] | null): NamePart[] {
  const parts = (format ?? []).filter((part): part is NamePart =>
    VALID_PARTS.has(part as NamePart),
  );
  return parts.length > 0 ? parts : [...DEFAULT_NAME_FORMAT];
}

export interface NameParts {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  maidenName?: string | null;
}

export function formatPersonName(
  person: NameParts,
  format?: string[] | null,
  options?: { transliterate?: boolean },
): string {
  const shouldTransliterate =
    options?.transliterate ?? useLocale.getState().locale === 'en';
  return normalizeNameFormat(format)
    .filter((part) => part !== 'relationship')
    .map((part) => {
      const value = person[part as keyof NameParts]?.trim();
      if (!value) return null;
      const display = shouldTransliterate
        ? transliterateCyrillic(value)
        : value;
      return part === 'maidenName' ? `(${display})` : display;
    })
    .filter(Boolean)
    .join(' ');
}

export function useNameFormat(): NamePart[] {
  const settings = useSession((state) => state.user?.settings);
  return useMemo(
    () => normalizeNameFormat(settings?.nameFormat),
    [settings?.nameFormat],
  );
}
